import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  startGetServiciosByCampania,
  startConsultaValidador,
  setCrearServiciosV2Captura,
  setCrearServiciosV2ClienteID,
  startGetListaV2,
  startQueryChubbV2,
  startQueryGSegurosV2,
  startQueryEdegaV2,
  cleanCrearServiciosV2ValidaInfo,
} from "../../../context/actions/crearServiciosV2Actions";
import { cleanCaptura } from "../../../context/actions/capturaActions";
import { cleanPolizario } from "../../../context/actions/polizarioActions";
import { useWizardState } from "./useWizardState";
import { useAutorizacion } from "./useAutorizacion";
import { tiposTextoValor } from "../../../utils/tiposTextoValor";
import { serviciosTextoValor } from "../../../utils/serviciosTextoValor";
import { CLIENTES_IDS } from "../../../constants/cons";
import { getAhoraCompleto } from "../../../utils/getAhoraCompleto";

export const useCrearServiciosV2 = (form) => {
  const dispatch = useDispatch();

  const { clientesCampanias, validaInfo, captura, servicios } = useSelector(
    (state) => state.asistenciasReducer
  );
  const { gsegurosEmision } = useSelector((state) => state.polizarioReducer);
  const [opcionesServicios, setOpcionesServicios] = useState([]);

  const fechaLlamadaIniciadaRef = useRef(getAhoraCompleto());

  const state = useWizardState();
  const { autorizarServicio, crearAsistenciaSinAutorizacion } = useAutorizacion();

  useEffect(() => {
    const validador2 = validaInfo?.asegurado?.validador2;
    if (!validador2 || captura.validador2 === validador2) return;
    dispatch(setCrearServiciosV2Captura({ validador2 }));
  }, [validaInfo, captura.validador2, dispatch]);

  const serviciosRelacionados = useMemo(() => {
    return (validaInfo?.servicios || []).map(
      (s) => `${s.asistenciaID} | ${s.fechaCreacion} | ${s.tipoAsistencia}-${s.servicio} | ${s.estatus}`
    );
  }, [validaInfo]);

  const serviciosRegistrados = validaInfo?.servicios?.length || 0;
  const requiereAutorizacion = serviciosRegistrados > 0;
  const opcionesTipos = useMemo(() => tiposTextoValor(servicios?.tipos || []), [servicios]);

  const handleTipoAsistenciaChange = (tipoAsistenciaID) => {
    form.setFieldsValue({ servicioID: undefined });
    const nuevos = servicios?.serviciosFinal?.filter((s) => s.tipoAsistenciaID === tipoAsistenciaID) || [];
    setOpcionesServicios(serviciosTextoValor(nuevos));
  };

  const handleClienteChange = (value) => {
    state.setClienteSeleccionado(value);
    state.setCampaniaSeleccionada(null);
    state.setMostrarFormularioValidador(false);
    state.setMostrarResultados(false);
    form.resetFields();
    dispatch(cleanCaptura());
    dispatch(cleanPolizario());
    dispatch(setCrearServiciosV2ClienteID(value));
  };

  const handleCampaniaChange = (value) => {
    state.setCampaniaSeleccionada(value);
    state.setMostrarFormularioValidador(true);
    state.setMostrarResultados(false);
    form.resetFields();
    dispatch(cleanCaptura());
    dispatch(cleanPolizario());
    dispatch(setCrearServiciosV2Captura({ campaniaID: value }));
    dispatch(startGetServiciosByCampania(value));
    setOpcionesServicios([]);
  };

  const handleBuscarRelacionados = async (values) => {
    state.setLoadingBusqueda(true);
    state.setMostrarResultados(true);

    // Si es General de Seguros, armar el validador1 desde los 4 campos
    let validador1 = values.validador1;
    if (state.clienteSeleccionado === CLIENTES_IDS.GS) {
      validador1 = `${values.sucursal}-${values.ramo}-${values.poliza}-${values.inciso}`;
    }

    if (state.clienteSeleccionado === CLIENTES_IDS.EDEGA || state.clienteSeleccionado === CLIENTES_IDS.EDEGA_PROD || state.clienteSeleccionado === CLIENTES_IDS.EDEGA_QA) {
      dispatch(cleanCrearServiciosV2ValidaInfo());
      const resultPolizario = await dispatch(startQueryEdegaV2(validador1));

      dispatch(setCrearServiciosV2Captura({
        noEncontradoPolizario: resultPolizario?.noEncontrado ?? false,
        polizarioBloqueado: resultPolizario?.bloqueado ?? false,
        ct_uid: resultPolizario?.ct_uid,
        validador1,
      }));

      state.setLoadingBusqueda(false);
      return;
    }

    if (state.clienteSeleccionado === CLIENTES_IDS.CHUBB) {
      dispatch(cleanCrearServiciosV2ValidaInfo());
      const resultPolizario = await dispatch(startQueryChubbV2(validador1));

      dispatch(setCrearServiciosV2Captura({
        noEncontradoPolizario: resultPolizario?.noEncontrado ?? false,
        validador1,
      }));

      state.setLoadingBusqueda(false);
      return;
    }

    const promesas = [dispatch(startConsultaValidador(validador1, state.campaniaSeleccionada))];

    if (state.clienteSeleccionado === CLIENTES_IDS.EL_AGUILA) {
      promesas.push(dispatch(startGetListaV2(validador1)));
    } else if (state.clienteSeleccionado === CLIENTES_IDS.GS) {
      const parts = (validador1 || "").split("-");
      if (parts.length === 4) {
        promesas.push(dispatch(startQueryGSegurosV2({
          sucursal: parts[0], ramo: parts[1], poliza: parts[2], inciso: parts[3],
        })));
      }
    }

    const [resultValidador, resultPolizario] = await Promise.all(promesas);
    state.setLoadingBusqueda(false);

    if (!resultValidador.success) {
      state.setMostrarResultados(false);
      return;
    }

    if (resultPolizario !== undefined) {
      dispatch(setCrearServiciosV2Captura({
        noEncontradoPolizario: resultPolizario?.noEncontrado ?? false,
        polizarioBloqueado: resultPolizario?.bloqueado ?? false,
        ct_uid: resultPolizario?.ct_uid,
      }));
    }

    if (gsegurosEmision?.agente) {
      dispatch(setCrearServiciosV2Captura({ agente: gsegurosEmision.agente }));
    }
    if (gsegurosEmision?.numero_serie) {
      dispatch(setCrearServiciosV2Captura({ numero_serie: gsegurosEmision.numero_serie }));
    }
  };

  const handleAutorizar = async () => {
    if (!captura.tipoAsistenciaID || !captura.servicioID) return;
    state.setLoadingAutorizacion(true);
    await autorizarServicio(state.claveSupervisor, fechaLlamadaIniciadaRef.current);
    state.setLoadingAutorizacion(false);
  };

  const handleGenerarOP = async () => {
    if (!captura.tipoAsistenciaID || !captura.servicioID) return;
    state.setLoadingAutorizacion(true);
    await crearAsistenciaSinAutorizacion(fechaLlamadaIniciadaRef.current);
    state.setLoadingAutorizacion(false);
  };

  const handleFormValuesChange = useCallback((changedValues, allValues) => {
    const { campaniaID: _cid, ...rest } = allValues;

    if ((state.clienteSeleccionado === CLIENTES_IDS.EDEGA || state.clienteSeleccionado === CLIENTES_IDS.EDEGA_PROD || state.clienteSeleccionado === CLIENTES_IDS.EDEGA_QA) && "validador1" in changedValues) {
      dispatch(cleanPolizario());
      dispatch(setCrearServiciosV2Captura({
        ...rest,
        noEncontradoPolizario: true,
        polizarioBloqueado: true,
        ct_uid: undefined,
      }));
      state.setMostrarResultados(false);
      return;
    }
    
    // Si es General de Seguros y todos los 4 campos están llenos, armar validador1
    if (state.clienteSeleccionado === CLIENTES_IDS.GS && rest.sucursal && rest.ramo && rest.poliza && rest.inciso) {
      const validador1 = `${rest.sucursal}-${rest.ramo}-${rest.poliza}-${rest.inciso}`;
      dispatch(setCrearServiciosV2Captura({ ...rest, validador1 }));
    } else {
      dispatch(setCrearServiciosV2Captura(rest));
    }
  }, [dispatch, state.clienteSeleccionado]);

  return {
    clientesCampanias,
    serviciosRelacionados,
    serviciosRegistrados,
    requiereAutorizacion,
    polizarioBloqueado: captura.polizarioBloqueado ?? false,
    opcionesTipos,
    opcionesServicios,
    campaniaSeleccionada: state.campaniaSeleccionada,
    clienteSeleccionado: state.clienteSeleccionado,
    claveSupervisor: state.claveSupervisor,
    mostrarFormularioValidador: state.mostrarFormularioValidador,
    mostrarResultados: state.mostrarResultados,
    loadingBusqueda: state.loadingBusqueda,
    loadingAutorizacion: state.loadingAutorizacion,
    handleClienteChange,
    handleCampaniaChange,
    handleBuscarRelacionados,
    handleAutorizar,
    handleGenerarOP,
    handleTipoAsistenciaChange,
    handleFormValuesChange,
    setClaveSupervisor: state.setClaveSupervisor,
  };
};
